// frontend/src/components/common/MedicalDeviceMarks.js
//
// Återanvändbara komponenter för medicinteknisk märkning (MDR 2017/745):
//   - <CeMark />      CE-märke (klass I – utan anmält organs nummer)
//   - <MdSymbol />    "MD"-symbol (medicinteknisk produkt, ISO 15223-1)
//   - <ProductLabel/> fullständig elektronisk etikett från PRODUCT_INFO
//
// CE-symbolens geometri är hämtad ordagrant ur den officiella vektorfilen
// (CE.ai, EU:s godkända proportioner). PostScript-koordinater (Y nedifrån-upp)
// speglas till SVG via transform. MD-symbolen följer ISO 15223-1.

import React from 'react';
import { PRODUCT_INFO } from '../../constants/productInfo';
import './MedicalDeviceMarks.css';

// Officiell CE-märkning. Bredd/höjd-förhållandet (1.4:1) följer vektorfilens
// bokstavsruta (840 x 600 enheter).
const CE_PATH =
  // E
  'M951 150.48 L951 241.13 C941.2 239.72 931.19 239 921 239 ' +
  'C820.46 239 736.43 309.64 715.83 404 L891 404 L891 494 L715.83 494 ' +
  'C736.43 588.36 820.46 659 921 659 C931.19 659 941.2 658.28 951 656.88 ' +
  'L951 747.52 C941.13 748.5 931.13 749 921 749 C755.31 749 621 614.69 621 449 ' +
  'C621 283.31 755.31 149 921 149 C931.13 149 941.13 149.5 951 150.48 Z ' +
  // C
  'M111 449 C111 283.31 245.31 149 411 149 C421.81 149 432.49 149.57 443 150.68 ' +
  'L443 241.42 C432.57 239.82 421.88 239 411 239 C295.02 239 201 333.02 201 449 ' +
  'C201 564.98 295.02 659 411 659 C421.88 659 432.57 658.18 443 656.58 ' +
  'L443 747.32 C432.49 748.43 421.81 749 411 749 C245.31 749 111 614.69 111 449 Z';

export function CeMark({ size = 28, title = 'CE-märkning' }) {
  return (
    <svg
      className="md-mark md-mark--ce"
      width={size * 1.4}
      height={size}
      viewBox="96 76 870 630"
      role="img"
      aria-label={title}
    >
      <title>{title}</title>
      <g transform="translate(0 840) scale(1 -1)">
        <path d={CE_PATH} fill="currentColor" />
      </g>
    </svg>
  );
}

export function MdSymbol({ size = 28, title = 'Medicinteknisk produkt' }) {
  return (
    <svg
      className="md-mark md-mark--md"
      width={size * 1.6}
      height={size}
      viewBox="0 0 80 50"
      role="img"
      aria-label={title}
    >
      <title>{title}</title>
      <rect x="3" y="3" width="74" height="44" rx="3" fill="none" stroke="currentColor" strokeWidth="4" />
      <text
        x="40"
        y="35"
        textAnchor="middle"
        fontSize="28"
        fontWeight="700"
        fontFamily="Arial, sans-serif"
        fill="currentColor"
      >
        MD
      </text>
    </svg>
  );
}

// Fullständig elektronisk etikett. Används i Kugghjulet ("Om appen") och
// kan återanvändas var som helst som behöver visa hela etiketten.
export function ProductLabel() {
  const p = PRODUCT_INFO;
  return (
    <div className="product-label">
      <div className="product-label-marks">
        <CeMark size={32} />
        <MdSymbol size={32} />
      </div>

      <dl className="product-label-fields">
        <div>
          <dt>Produkt</dt>
          <dd>{p.name}</dd>
        </div>
        <div>
          <dt>Typ</dt>
          <dd>{p.productType}</dd>
        </div>
        <div>
          <dt>Version</dt>
          <dd>{p.version} ({p.versionDate})</dd>
        </div>
        <div>
          <dt>Riskklass</dt>
          <dd>Klass {p.riskClass} – {p.classificationRule}, MDR 2017/745</dd>
        </div>
        <div>
          <dt>Tillverkare</dt>
          <dd>
            {p.manufacturer.name} (org.nr {p.manufacturer.orgNumber})
            <br />
            {p.manufacturer.address}
          </dd>
        </div>
        <div>
          <dt>Kontakt</dt>
          <dd>
            {p.manufacturer.email}
            {p.manufacturer.phone && (
              <>
                <br />
                {p.manufacturer.phone}
              </>
            )}
          </dd>
        </div>
        <div>
          <dt>UDI-DI</dt>
          <dd>{p.udiDi}</dd>
        </div>
        <div>
          <dt>Basic UDI-DI</dt>
          <dd>{p.basicUdiDi}</dd>
        </div>
        <div>
          <dt>SRN</dt>
          <dd>{p.srn}</dd>
        </div>
        <div>
          <dt>Avsett ändamål</dt>
          <dd>{p.intendedPurpose}</dd>
        </div>
      </dl>
    </div>
  );
}
